import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { VISITOR_TYPE_HOSTS_QUERY_KEY } from "./useVisitorTypeHosts";
import { VISITOR_STATION_QUERY_KEY } from "./useVisitorStation";
import { VISITOR_TYPES_QUERY_KEY } from "./useVisitorTypes";

/**
 * Hook to invalidate visitor type hosts queries.
 * Use this after changes that might affect the hosts list.
 */
export const useInvalidateVisitorTypeHosts = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (visitorTypeId?: string) => {
      if (visitorTypeId) {
        await queryClient.invalidateQueries({
          queryKey: [VISITOR_TYPE_HOSTS_QUERY_KEY, visitorTypeId]
        });
      } else {
        await queryClient.invalidateQueries({
          queryKey: [VISITOR_TYPE_HOSTS_QUERY_KEY]
        });
      }
    },
    [queryClient]
  );
};

/**
 * Hook to invalidate visitor station queries.
 * Use this after changes that might affect station data.
 */
export const useInvalidateVisitorStation = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (stationId?: string) => {
      if (stationId) {
        await queryClient.invalidateQueries({
          queryKey: [VISITOR_STATION_QUERY_KEY, stationId]
        });
      } else {
        await queryClient.invalidateQueries({
          queryKey: [VISITOR_STATION_QUERY_KEY]
        });
      }
    },
    [queryClient]
  );
};

/**
 * Hook to invalidate visitor types queries.
 * Use this after changes that might affect visitor types.
 */
export const useInvalidateVisitorTypes = () => {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [VISITOR_TYPES_QUERY_KEY]
    });
  }, [queryClient]);
};

/**
 * Hook to invalidate all kiosk-related queries.
 * Use this when the kiosk needs a full refresh.
 */
export const useInvalidateAllKiosk = () => {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [VISITOR_TYPE_HOSTS_QUERY_KEY]
      }),
      queryClient.invalidateQueries({
        queryKey: [VISITOR_STATION_QUERY_KEY]
      }),
      queryClient.invalidateQueries({
        queryKey: [VISITOR_TYPES_QUERY_KEY]
      })
    ]);
  }, [queryClient]);
};
